import { z } from 'zod';
import { CalendarDate, ProjectMethodology, ProjectStatus } from './project.js';
/**
 * Dashboard phân tầng & view cấp portfolio — APM-804, spec 16.5.
 *
 * ## Ba tầng, ba câu hỏi khác nhau
 *
 * Spec 16.6 nói thẳng: **không dùng chung một dashboard cho cả ba**. Dev cần
 * biết *làm gì tiếp theo*; PM cần biết *ai đang tắc, gì sắp trễ*; lãnh đạo cần
 * biết *dự án nào có vấn đề*. Gộp lại thành một trang thì mỗi người phải bỏ qua
 * hai phần ba màn hình, và phần họ cần bị đẩy xuống dưới.
 *
 * ## Vì sao RAG luôn đi kèm lý do
 *
 * Một ô đỏ không nói vì sao đỏ thì người đọc phải mở từng biểu đồ để tự đoán —
 * và họ sẽ đoán sai. Mọi `ProjectHealth` ở đây mang theo **ba tín hiệu thành
 * phần** (tiến độ, rủi ro, độ trễ) kèm con số đã dùng để kết luận.
 */
export const RagStatus = z.enum(['red', 'amber', 'green', 'unknown']);
// ── Portfolio roadmap ───────────────────────────────────────────────────────
export const RoadmapMilestone = z.object({
    taskId: z.uuid(),
    taskKey: z.string(),
    title: z.string(),
    /** `milestone` hoặc `phase_gate` — hai thứ khác nhau trên cùng một trục. */
    kind: z.enum(['milestone', 'phase_gate']),
    date: CalendarDate,
    passed: z.boolean(),
    /** Trễ so với baseline, ngày. `null` = chưa chốt baseline. */
    slippageDays: z.number().nullable(),
});
export const RoadmapProject = z.object({
    projectId: z.uuid(),
    key: z.string(),
    name: z.string(),
    status: ProjectStatus,
    methodology: ProjectMethodology,
    /** `null` = dự án chưa đặt ngày; UI xếp nó ra khu vực "chưa lên lịch". */
    startDate: CalendarDate.nullable(),
    endDate: CalendarDate.nullable(),
    milestones: z.array(RoadmapMilestone),
});
export const PortfolioRoadmap = z.object({
    from: CalendarDate,
    to: CalendarDate,
    asOf: z.iso.datetime().nullable(),
    projects: z.array(RoadmapProject),
    /** Dự án chưa đặt ngày bắt đầu/kết thúc — không vẽ được lên trục thời gian. */
    unscheduledCount: z.number().int().nonnegative(),
});
// ── Health board ────────────────────────────────────────────────────────────
/**
 * Một tín hiệu thành phần của RAG, kèm con số đã dùng để kết luận.
 *
 * `detail` là dữ liệu, không phải câu chữ: bản dịch nằm ở frontend (luật i18n),
 * còn ở đây chỉ có mã tín hiệu và số.
 */
export const HealthSignal = z.object({
    code: z.enum(['schedule', 'risk', 'progress']),
    status: RagStatus,
    /** Con số quyết định: số task trễ, điểm rủi ro cao nhất, hoặc SPI ×100. */
    value: z.number().nullable(),
});
export const ProjectHealth = z.object({
    projectId: z.uuid(),
    key: z.string(),
    name: z.string(),
    status: ProjectStatus,
    /** Xấu nhất trong ba tín hiệu — một dự án chỉ "xanh" khi cả ba đều xanh. */
    overall: RagStatus,
    signals: z.array(HealthSignal),
    taskCount: z.number().int().nonnegative(),
    doneCount: z.number().int().nonnegative(),
    lateTaskCount: z.number().int().nonnegative(),
    openRiskCount: z.number().int().nonnegative(),
    /** Điểm rủi ro cao nhất còn mở, thang 1–25. `null` = chưa rủi ro nào có điểm. */
    topRiskScore: z.number().int().nullable(),
    /** Mốc gần nhất phía trước, để lãnh đạo biết cái gì đang tới. */
    nextMilestone: RoadmapMilestone.nullable(),
});
export const PortfolioHealth = z.object({
    asOf: z.iso.datetime().nullable(),
    projects: z.array(ProjectHealth),
});
// ── Capacity vs demand ─────────────────────────────────────────────────────
export const CapacityBucket = z.object({
    /** Thứ Hai của tuần. */
    date: CalendarDate,
    demandHours: z.number().nonnegative(),
    capacityHours: z.number().nonnegative(),
});
/**
 * Nhu cầu so với năng lực của **toàn tổ chức** theo tuần.
 *
 * Chỉ có con số tổng, không có tên ai: đây là lý do view này không cần quyền
 * nhạy cảm. Phần chia theo từng người nằm ở `CrossProjectAllocation`, và nó có
 * cái giá của nó (`sensitive_access_log`).
 */
export const CapacityForecast = z.object({
    from: CalendarDate,
    to: CalendarDate,
    buckets: z.array(CapacityBucket),
    headcount: z.number().int().nonnegative(),
    /** Số tuần mà nhu cầu vượt năng lực — con số duy nhất cần hành động. */
    overloadedWeekCount: z.number().int().nonnegative(),
});
// ── Cross-project allocation ───────────────────────────────────────────────
export const AllocationRow = z.object({
    userId: z.uuid(),
    displayName: z.string(),
    /** Số dự án người này đang có việc chưa xong. */
    projectCount: z.number().int().nonnegative(),
    committedHours: z.number().nonnegative(),
    capacityHours: z.number().nonnegative(),
    /** `committed / capacity × 100`. > 100 là đã hứa nhiều hơn số giờ có thật. */
    allocationPct: z.number().nonnegative(),
});
/**
 * Một người đang ở bao nhiêu dự án và tổng phân bổ bao nhiêu — spec 16.5.
 *
 * ⚠️ **Dữ liệu nhạy cảm.** Nó có tên người kèm con số dùng để điều phối, nên
 * đọc nó cần `performance:read_cross_project` và **mỗi lần đọc ghi
 * `sensitive_access_log`** cho từng người bị xem (docs/security.md §5). Người
 * bị xem có quyền biết ai đã mở hồ sơ của mình.
 */
export const CrossProjectAllocation = z.object({
    from: CalendarDate,
    to: CalendarDate,
    rows: z.array(AllocationRow),
    overCommittedCount: z.number().int().nonnegative(),
});
// ── Activity heatmap ───────────────────────────────────────────────────────
export const ActivityRow = z.object({
    userId: z.uuid(),
    displayName: z.string(),
    /** Số lần cập nhật mỗi ngày, cùng thứ tự với `ActivityHeatmap.days`. */
    counts: z.array(z.number().int().nonnegative()),
    total: z.number().int().nonnegative(),
});
/**
 * Mức độ cập nhật tiến độ theo người theo ngày — spec 16.5.
 *
 * ⚠️ Cũng là **dữ liệu hiệu suất cá nhân**: "ai ít cập nhật nhất" là một câu
 * hỏi về con người. `performance:read_project` + audit log, không phải
 * `report:read_project`.
 *
 * Đếm **event**, không đếm task: mục đích của biểu đồ là đo *thói quen cập
 * nhật* (spec mục 13), và một người kéo thẻ mười lần trong ngày đang cập nhật
 * liên tục kể cả khi chưa xong task nào.
 */
export const ActivityHeatmap = z.object({
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    asOf: z.iso.datetime().nullable(),
    days: z.array(CalendarDate),
    rows: z.array(ActivityRow),
    /** Ô có giá trị lớn nhất — để frontend chuẩn hoá thang màu. */
    peak: z.number().int().nonnegative(),
});
// ── Dashboard cá nhân ──────────────────────────────────────────────────────
export const DashboardTask = z.object({
    taskId: z.uuid(),
    taskKey: z.string(),
    title: z.string(),
    projectName: z.string(),
    dueDate: CalendarDate.nullable(),
    statusCategory: z.enum(['todo', 'in_progress', 'done']),
    priority: z.enum(['low', 'medium', 'high', 'urgent']),
    isBlocked: z.boolean(),
    blockReason: z.string().nullable(),
});
/**
 * Dashboard của một người — tầng "dev" của spec 16.5.
 *
 * Bốn nhóm, xếp theo thứ tự **cần hành động**: đang bị chặn (không tự làm gì
 * được), quá hạn, đến hạn hôm nay, sắp tới. Xếp theo ngày tạo hay theo dự án
 * thì người dùng phải tự tìm ra việc cần làm trước — mà đó chính là việc mà
 * dashboard này phải làm thay họ.
 */
export const MyDashboard = z.object({
    blocked: z.array(DashboardTask),
    overdue: z.array(DashboardTask),
    dueToday: z.array(DashboardTask),
    upcoming: z.array(DashboardTask),
    /** Tổng task chưa xong đang được giao, kể cả những task không nằm bốn nhóm trên. */
    openCount: z.number().int().nonnegative(),
    doneThisWeek: z.number().int().nonnegative(),
    /** Giờ đã cam kết trong 7 ngày tới so với năng lực. */
    committedHours: z.number().nonnegative(),
    capacityHours: z.number().nonnegative(),
});
//# sourceMappingURL=portfolio.js.map